class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.toggleDesktopMenuItem = this.toggleDesktopMenuItem.bind(this);
    this.closeMenuIfClickOutside = this.closeMenuIfClickOutside.bind(this);
    this.closeOpenMenuItem = this.closeOpenMenuItem.bind(this);

    this.initVariables();
    this.addDesktopMenuTopLinksEventListeners();
    this.addMobileMenuEventListeners();

    this.setTopBarHeightVars();
    this.initEventListeners();
  }

  initVariables() {
    this.desktopMenuTopLinks = this.querySelectorAll('[data-top-bar-link]');
    this.mobileMenuDetails = this.querySelectorAll(
      '[data-mobile-header-menu] details'
    );
    this.topBar = this.querySelector('[data-top-bar]');
    this.searchCTA = this.querySelector('[data-search-cta]');
  }

  initEventListeners() {
    this.searchCTA?.addEventListener('click', () => {
      const searchForm = document.querySelector(
        '[data-predictive-search-input]'
      );
      searchForm.focus();
      // Close out any open menus when search is opened
      this.closeOpenMenuItem();
    });

    this.calculateNavHeight();
    window.addEventListener('scroll', this.calculateBottomOfTopBar.bind(this));
  }

  /**
   * Calculates the height of the header and sets it as a CSS variable.
   * @returns {void}
   */
  calculateNavHeight() {
    if (!this.topBar) return;

    const headerHeight = this.topBar.getBoundingClientRect().height;
    document.documentElement.style.setProperty(
      '--header-height',
      `${headerHeight}px`
    );
  }

  /**
   * Adds click event listeners to desktop menu top links.
   */
  addDesktopMenuTopLinksEventListeners() {
    for (const link of this.desktopMenuTopLinks) {
      link.addEventListener('click', this.toggleDesktopMenuItem);
      link.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          this.toggleDesktopMenuItem(event);
        }
      });
    }
  }

  /**
   * Returns the menu content element with the given identifier.
   * @param {String} identifier
   * @returns
   */
  getMenuContentElement(identifier) {
    return this.querySelector(`[data-menu-content=${identifier}]`);
  }

  /**
   * Returns the menu top link element with the given identifier.
   * @param {String} identifier
   * @returns
   */
  getMenuTopLinkElement(identifier) {
    return this.querySelector(`[data-menu-item=${identifier}]`);
  }

  /**
   * Toggles the visibility of the menu content element
   * @param {Event} event
   */
  toggleDesktopMenuItem(event) {
    event.preventDefault();
    const clickedLink = event.currentTarget;
    const contentIdentifier = clickedLink.dataset.menuItem;

    // Use the menu buttons as the source of state for the menu content
    clickedLink.hasAttribute('aria-expanded')
      ? this.closeMenu(contentIdentifier)
      : this.openMenu(contentIdentifier);
  }

  /**
   * Opens the menu content element with the given identifier.
   * @param {String} contentIdentifier
   * @returns {void}
   */
  openMenu(contentIdentifier) {
    this.closeOpenMenuItem();
    const menuTopLink = this.getMenuTopLinkElement(contentIdentifier);
    const menuContent = this.getMenuContentElement(contentIdentifier);

    if (!menuContent) return;

    menuTopLink.setAttribute('aria-expanded', 'true');
    menuTopLink.classList.add('active');
    menuContent.removeAttribute('inert');
    this.addBodyEventListeners();
    // Focus on the first link in the menu content
    menuContent.querySelector('a').focus();
  }

  /**
   * Closes the menu content element with the given identifier.
   * @param {String} contentIdentifier
   */
  closeMenu(contentIdentifier) {
    const menuContent = this.getMenuContentElement(contentIdentifier);
    const menuTopLink = this.getMenuTopLinkElement(contentIdentifier);
    menuTopLink.removeAttribute('aria-expanded');
    menuTopLink.classList.remove('active');
    menuContent.setAttribute('inert', '');
    this.removeBodyEventListeners();
  }

  /**
   * Closes the currently open menu item.
   * @returns {void}
   */
  closeOpenMenuItem() {
    const openMenu = this.querySelector('[aria-expanded="true"]');
    if (openMenu) {
      this.closeMenu(openMenu.dataset.menuItem);
    }
  }

  /**
   * Adds event listeners to the body element.
   * @returns {void}
   */
  addBodyEventListeners() {
    document.body.addEventListener('click', this.closeMenuIfClickOutside);
  }

  /**
   * Removes event listeners from the body element.
   * @returns {void}
   */
  removeBodyEventListeners() {
    document.body.removeEventListener('click', this.closeMenuIfClickOutside);
  }

  /**
   * Closes the currently open menu item if the click event target is outside the menu
   * @param {Event} event
   */
  closeMenuIfClickOutside(event) {
    if (!this.topBar.contains(event.target)) {
      this.closeOpenMenuItem();
    }
  }

  /**
   * Adds event listeners to mobile menu details elements.
   * Prevents default behavior and adds a closing animation when a details element is clicked.
   * Removes the closing animation and closes the element when the animation ends.
   */
  addMobileMenuEventListeners() {
    for (const details of this.mobileMenuDetails) {
      details.addEventListener('click', event => {
        if (details.hasAttribute('open') && event.target.closest('summary')) {
          // since it's not closed yet, it's open!
          event.preventDefault(); // stop the default behavior, meaning - the hiding
          details.classList.add('closing'); // add a class which applies the animation in CSS
        }
      });

      // when the "close" animation is over
      details.addEventListener('animationend', event => {
        if (event.animationName === 'close-details') {
          details.removeAttribute('open'); // close the element
          details.classList.remove('closing'); // remove the animation
        }
      });
    }
  }

  /**
   * Calculates the height of the top bar and sets it as CSS variables.
   * @returns {void}
   */
  setTopBarHeightVars() {
    // This size will change when the menu is opened
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const bottomOfTopBar = entry.target.getBoundingClientRect().bottom;
        document.documentElement.style.setProperty(
          '--bottom-of-top-bar',
          `${bottomOfTopBar}px`
        );
      }
    });

    resizeObserver.observe(this.topBar);

    window.addEventListener('resize', this.calculateBottomOfTopBar.bind(this));
    window.addEventListener('resize', this.calculateNavHeight.bind(this));
  }

  /**
   * Calculates the bottom of the top bar and sets it as a CSS variable.
   * @returns {void}
   */
  calculateBottomOfTopBar() {
    const bottomOfTopBar = this.topBar.getBoundingClientRect().bottom;
    document.documentElement.style.setProperty(
      '--bottom-of-top-bar',
      `${bottomOfTopBar}px`
    );
  }
}

customElements.get('site-header') ||
  customElements.define('site-header', SiteHeader);
